import { FC, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useOrganizationStore, useAskRemiEnabled } from '../../stores/useOrganizationStore';
import { useAuthStore } from '../../contexts/auth/useAuthStore';
import { useUser } from '../../hooks/useUser';
import type { UserRole } from '../../hooks/graphql/users/useUser';
import { FullPageLoader } from '../common/FullPageLoader';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: UserRole;
}

export const ProtectedRoute: FC<ProtectedRouteProps> = ({ children, requiredRole }) => {
  const { isAuthenticated, loading: orgLoading, organization, error } = useOrganizationStore();
  const { user: authUser, loading: authLoading } = useAuthStore();
  const { isSuperAdmin, isAdmin, loading: userLoading } = useUser();
  const { isLoading: askRemiLoading } = useAskRemiEnabled();
  const location = useLocation();

  // Show loading state while auth, org, user, or the per-org Ask Remi flag is
  // still loading. Including the flag here prevents the dashboard chat trigger
  // from flashing on first paint while the GET is in flight.
  if (authLoading || orgLoading || userLoading || askRemiLoading) {
    return <FullPageLoader />;
  }

  // Only redirect to login if we're certain the user is not authenticated
  if (!authLoading && (!authUser || !isAuthenticated || !organization || error)) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Role-gated route (e.g. /client-administration → super_admin only).
  // Authenticated users without the required role bounce to /dashboard, not /login.
  if (requiredRole) {
    const roleAllowed =
      requiredRole === 'super_admin' ? isSuperAdmin :
      requiredRole === 'org_admin' ? isAdmin :
      true; // 'agent' — any authenticated user
    if (!roleAllowed) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  // Check onboarding completion status
  // If onboardingComplete is explicitly false, require onboarding
  // If onboardingComplete doesn't exist (old clients) or is true, treat as complete
  const isOnboardingComplete = organization?.isOnboardingComplete !== false;
  const isOnOnboardingPage = location.pathname === '/onboarding';

  // Redirect away from onboarding if already complete or is super admin
  // This check must come BEFORE the redirect TO onboarding to prevent flash for admins
  if ((isSuperAdmin || isOnboardingComplete) && isOnOnboardingPage) {
    return <Navigate to="/dashboard" replace />;
  }

  // Redirect non-super-admins to onboarding if not complete
  if (!isSuperAdmin && !isOnboardingComplete && !isOnOnboardingPage) {
    return <Navigate to="/onboarding" replace />;
  }

  // Check if user needs to complete the Revive flow onboarding guide
  // Only redirect if:
  // 1. Onboarding is complete (isOnboardingComplete === true, not just !== false)
  // 2. reviveTourCompleted is EXPLICITLY false (not missing or true)
  // 3. User is not already on the revive flows page
  // Note: If the field doesn't exist (old clients), we DON'T redirect - only affects new onboarding users
  // Flows use hash routing: /flows#engage, /flows#convert, /flows#revive
  const isOnReviveFlowsPage =
    location.pathname === '/flows' && (location.hash === '' || location.hash === '#revive');

  // Only redirect if reviveTourCompleted is explicitly set to false
  // This way old clients without this field won't be affected.
  // Accept either camelCase (current writers) or snake_case (legacy).
  const setup = (organization as { onboardingSetup?: { reviveTourCompleted?: boolean; revive_tour_completed?: boolean } } | null)?.onboardingSetup;
  const needsReviveTour =
    setup?.reviveTourCompleted === false || setup?.revive_tour_completed === false;

  if (
    !isSuperAdmin &&
    organization?.isOnboardingComplete === true &&
    needsReviveTour &&
    !isOnReviveFlowsPage
  ) {
    return <Navigate to="/flows#revive" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
